import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Shield, Swords, Users, Map, Target } from 'lucide-react';

const HERO_BG = 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/995563def0829ec15c5d8053a34d58224241655108b605cb9d4f96fecf32375b.jpeg';

const CHAR_PREVIEWS = [
  { name: 'ANTON', title: 'The Reaper', faction: 'CMD', color: '#8B1A1A', img: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/ia3znv31_5CD90DB8-C183-4A0C-9DA3-2FD9E7017932.png', facePos: '45% 15%' },
  { name: 'VINCENT', title: 'The Consigliere', faction: 'CMD', color: '#4A7AB5', img: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/sjo3u2tp_D1F96B70-1EFD-49BD-A185-6D6D5B878E99.png', facePos: '45% 20%' },
  { name: 'SANTO', title: 'The Architect', faction: 'DDN', color: '#FF6B35', img: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/egeyfgma_BCC0332F-ED57-4438-957A-B139C1F6A695.png', facePos: '50% 15%' },
  { name: 'FILOMENA', title: 'The Shadow', faction: 'MORELLI', color: '#9F7AEA', img: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/9yst7d1k_D12DBCEE-F26D-41E6-AFB6-7E0D17125B53.png', facePos: '40% 15%' },
  { name: 'RODRIGUEZ', title: 'The Commissioner', faction: 'THE BADGE', color: '#1E90FF', img: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/1rdsrjox_055B5680-9953-419A-9817-53E0E4796821.png', facePos: '55% 25%' },
];

const FEATURES = [
  { icon: Swords, label: '10 PLAYABLE CHARACTERS', sub: '4 factions. Your path. Your war.' },
  { icon: Map, label: '228 NYC TERRITORIES', sub: 'Real locations. Real battles.' },
  { icon: Target, label: 'POLICE VS CRIMINAL', sub: 'Play the law or break it.' },
  { icon: Users, label: 'MULTIPLAYER WARS', sub: 'Territory control. Real-time.' },
  { icon: Shield, label: '4 ERAS OF CRIME', sub: 'From 1970 to present day.' },
];

const GameSection = () => {
  const [hoveredChar, setHoveredChar] = useState(null);

  return (
    <section
      id="game"
      data-testid="game-section"
      className="relative overflow-hidden"
    >
      {/* Hero background */}
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/70 to-[#050505]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/90 via-transparent to-[#050505]/90" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-32">
        {/* Game Logo + Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16"
        >
          <img
            src="/kow-game-logo.png"
            alt="Killing Over Water"
            className="h-36 sm:h-52 lg:h-64 mx-auto mb-8"
            data-testid="game-section-logo"
          />
          <div className="inline-block px-4 py-1.5 border border-[#C9A84C]/30 rounded-full mb-5">
            <span className="font-['Outfit'] text-[#C9A84C] text-[10px] sm:text-xs tracking-[0.3em]">
              THE INTERACTIVE EXPERIENCE
            </span>
          </div>
          <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/50 text-lg sm:text-xl italic max-w-md mx-auto">
            "The water remembers every move you make."
          </p>
        </motion.div>

        {/* Character Showcase */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-12 sm:mb-16"
        >
          <div className="flex justify-center gap-2 sm:gap-4">
            {CHAR_PREVIEWS.map((c, i) => (
              <motion.div
                key={c.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * i, duration: 0.5 }}
                className="relative group cursor-pointer"
                onMouseEnter={() => setHoveredChar(i)}
                onMouseLeave={() => setHoveredChar(null)}
                data-testid={`game-char-${c.name.toLowerCase()}`}
              >
                <div className={`relative w-16 h-24 sm:w-28 sm:h-40 rounded-lg overflow-hidden border-2 transition-all duration-500 ${
                  hoveredChar === i ? 'scale-110 shadow-lg shadow-black/50' : 'scale-100'
                }`} style={{ borderColor: hoveredChar === i ? c.color : c.color + '30' }}>
                  <img
                    src={c.img}
                    alt={c.name}
                    className={`w-full h-full object-cover transition-all duration-500 ${
                      hoveredChar !== null && hoveredChar !== i ? 'grayscale brightness-50' : 'grayscale-0'
                    }`}
                    style={{ objectPosition: c.facePos }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
                  <div className="absolute bottom-1 sm:bottom-2 left-0 right-0 text-center">
                    <span className="font-['Outfit'] text-[7px] sm:text-[9px] tracking-widest block" style={{ color: c.color }}>
                      {c.name}
                    </span>
                  </div>
                </div>
                {/* Hover tooltip */}
                {hoveredChar === i && (
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute -bottom-10 left-1/2 -translate-x-1/2 whitespace-nowrap hidden sm:block"
                  >
                    <span className="font-['Outfit'] text-[10px] tracking-wider px-2 py-1 rounded bg-black/80 border" style={{ color: c.color, borderColor: c.color + '40' }}>
                      {c.title} — {c.faction}
                    </span>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
          <p className="font-['Outfit'] text-[#E8E0D0]/25 text-[10px] sm:text-xs tracking-widest text-center mt-6 sm:mt-8">
            10 CHARACTERS ACROSS 4 FACTIONS — CMD / MORELLI FAMILY / DDN / THE BADGE
          </p>
        </motion.div>

        {/* Feature Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4 mb-12 sm:mb-16 max-w-4xl mx-auto"
        >
          {FEATURES.map((f, i) => (
            <div
              key={f.label}
              className={`text-center py-4 sm:py-5 px-2 rounded-lg bg-[#0a0e12]/60 border border-[#C9A84C]/8 backdrop-blur-sm ${
                i === 4 ? 'col-span-2 sm:col-span-1' : ''
              }`}
            >
              <f.icon size={18} className="text-[#C9A84C]/60 mx-auto mb-2" />
              <span className="font-['Outfit'] text-[#E8E0D0] text-[9px] sm:text-[10px] tracking-wider font-semibold block">
                {f.label}
              </span>
              <span className="font-['Outfit'] text-[#E8E0D0]/30 text-[8px] sm:text-[9px] block mt-0.5">
                {f.sub}
              </span>
            </div>
          ))}
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-center"
        >
          <a
            href="/game"
            className="group inline-flex items-center gap-3 px-10 sm:px-14 py-4 sm:py-5 bg-[#C9A84C]/10 hover:bg-[#C9A84C]/20 border border-[#C9A84C]/50 hover:border-[#C9A84C] rounded-sm transition-all duration-500"
            data-testid="play-game-btn"
          >
            <span className="font-['Cormorant_Garamond'] text-[#C9A84C] text-lg sm:text-xl tracking-[0.2em] font-bold">
              ENTER THE GAME
            </span>
            <ChevronRight size={20} className="text-[#C9A84C] group-hover:translate-x-1 transition-transform duration-300" />
          </a>
          <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest mt-4">
            FREE TO PLAY — NO ACCOUNT REQUIRED
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default GameSection;
